const STORAGE_KEY = "pantryverse-theme";
type ThemeName = "light" | "dark";

const prefersDark = () =>
  window.matchMedia("(prefers-color-scheme: dark)").matches;

const getStoredTheme = (): ThemeName | null => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === "light" || stored === "dark" ? stored : null;
  } catch {
    return null;
  }
};

const persistTheme = (theme: ThemeName) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Ignore storage errors; runtime theme should still apply
  }
};

export const applyTheme = (theme: ThemeName) => {
  document.documentElement.dataset.theme = theme;
  persistTheme(theme);
};

export const initTheme = () => {
  const initial = getStoredTheme() ?? (prefersDark() ? "dark" : "light");
  applyTheme(initial);

  // Keep in sync with system changes when there is no explicit override.
  const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
  const listener = (event: MediaQueryListEvent) => {
    if (!getStoredTheme()) {
      applyTheme(event.matches ? "dark" : "light");
    }
  };

  mediaQuery.addEventListener("change", listener);

  return () => mediaQuery.removeEventListener("change", listener);
};

export const toggleTheme = () => {
  const next =
    document.documentElement.dataset.theme === "dark" ? "light" : "dark";
  applyTheme(next as ThemeName);
  return next as ThemeName;
};
